import {
  Component,
  effect,
  input,
  signal,
  ViewChild,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { take } from 'rxjs/operators';
import {
  PatientTargetModel,
  GraphDataModel,
  GraphGrouping,
  TargetGraphType,
  TargetType,
  DataTakerStaffDataModel,
  GraphAnnotationModel,
} from '../../../core/api/models';
import { DatePipe } from '@angular/common';
import { MultiSelectChangeEvent } from 'primeng/multiselect';
import {
  GraphApiService,
  SupervisorApiService,
  StaffApiService,
} from '../../../core/api/services';
import { AlertService } from '../../../shared/services/alert.service';
import { Utils } from '../../../core/utils';
import { GraphsBaseComponent, SeriesDataLabelOption, TrendLineDataLabelOption, AnnotationLabelBackgroundOption } from '../../graphs-base.component';
import { ApexMarkers, ChartComponent } from 'ng-apexcharts';
import { Permissions } from '../../../core/api/models';
import { AuthService } from '../../../auth/auth.service';
import {
  PrintService,
  PrintHeaderInfo,
} from '../../../shared/services/print.service';

@Component({
  selector: 'app-target-graph',
  templateUrl: './target-graph.component.html',
  styleUrl: './target-graph.component.css',
  standalone: false,
})
export class TargetGraphComponent extends GraphsBaseComponent {
  @ViewChild('chart', { static: false }) chart: ChartComponent | undefined;
  @ViewChildren(ChartComponent) chartList!: QueryList<ChartComponent>;

  showAddAnnotation = signal<boolean>(false);
  annotationToEdit = signal<GraphAnnotationModel | null>(null);

  patientID = input.required<string>();
  patientIDAsNumber: number | null = null;

  startDate = signal<Date>(Utils.dateInPastBy(90));
  endDate = signal<Date>(new Date());
  forceGraphPeriod = signal<boolean>(true);

  isLoadingGraph: boolean = false;

  selectedTargets = input.required<PatientTargetModel[]>();

  staffMembers = signal<DataTakerStaffDataModel[]>([]);
  selectedStaffMember: DataTakerStaffDataModel | null = null;

  graphData = signal<GraphDataModel | null>(null);

  graphTypes: any[] = [];
  selectedGraphType: TargetGraphType | null = null;

  seriesDataLabelOption = signal<SeriesDataLabelOption>(
    SeriesDataLabelOption.DotsAndValues
  );
  trendLineDataLabelOption = signal<TrendLineDataLabelOption>(
    TrendLineDataLabelOption.LineOnly
  );

  seriesDataLabelOptions = [
    {
      label: SeriesDataLabelOption.LineOnly,
      value: SeriesDataLabelOption.LineOnly,
    },
    { label: SeriesDataLabelOption.Dots, value: SeriesDataLabelOption.Dots },
    {
      label: SeriesDataLabelOption.DotsAndValues,
      value: SeriesDataLabelOption.DotsAndValues,
    },
    {
      label: SeriesDataLabelOption.FirstAndLastValues,
      value: SeriesDataLabelOption.FirstAndLastValues,
    },
  ];

  trendLineDataLabelOptions = [
    {
      label: TrendLineDataLabelOption.LineOnly,
      value: TrendLineDataLabelOption.LineOnly,
    },
    {
      label: TrendLineDataLabelOption.Dots,
      value: TrendLineDataLabelOption.Dots,
    },
    {
      label: TrendLineDataLabelOption.DotsAndValues,
      value: TrendLineDataLabelOption.DotsAndValues,
    },
    {
      label: TrendLineDataLabelOption.FirstAndLastValues,
      value: TrendLineDataLabelOption.FirstAndLastValues,
    },
  ];
  displayLegend = signal<boolean>(true);

  displayMoreDataOnTooltips = signal<boolean>(true);

  displayTrendLine = signal<boolean>(false);
  segmentTrendLineByPhase = signal<boolean>(false);

  showNotTrackedAsZeroes = signal<boolean>(false);

  mergeSimilarPrompts = signal<boolean>(false);

  private dateChangeTimer: any;

  isPrintingPdf = false;
  isPrintingPng = false;

  constructor(
    private graphApiService: GraphApiService,
    private supervisorApiService: SupervisorApiService,
    private staffApiService: StaffApiService,
    private datePipe: DatePipe,
    private alertService: AlertService,
    private authService: AuthService,
    private printService: PrintService
  ) {
    super();
    this.graphExportService = printService;
    this.loadDefaultGraphOptions();

    // Set default date option to 3 months (index 6)
    this.dateOption = this.dateOptions[6].value;

    effect(() => {
      this.onSelectedTargetsChange();
    });
  }

  onSelectedTargetsChange() {
    if (this.selectedTargets().length > 0) {
      console.log(
        'targetId changed to:',
        this.selectedTargets()[0]?.ID + ' at ' + new Date().toISOString()
      );
      if (
        this.selectedTargets()[0]?.TargetType == TargetType.GroupedFrequency
      ) {
        this.selectedGraphType = TargetGraphType.GroupedFrequency;
      } else if (this.selectedTargets()[0]?.TargetType == TargetType.Anecdotal) {
        this.selectedGraphType = TargetGraphType.Anecdotal;
      } else if (
        this.selectedTargets()[0]?.TargetType ==
        TargetType.ConditionalDiscrimination
      ) {
        this.selectedGraphType = TargetGraphType.ConditionalDescrimination;
      }

      // update groupingOptions according to the selected target type
      if (this.selectedTargets()[0]?.TargetType == TargetType.Currency) {
        this.groupingOptions =
          this.groupingOptionsExcludingStaffOptionWithByTrial;
        this.groupingOption = GraphGrouping.ByTrial;
      } else if (
        this.selectedTargets()[0]?.TargetType == TargetType.Quantity ||
        this.selectedTargets()[0]?.TargetType == TargetType.Duration
      ) {
        this.groupingOptions =
          this.groupingOptionsIncludingStaffOptionAndByTrial;
      } else {
        this.groupingOptions = this.groupingOptionsIncludingStaffOption;
      }

      this.loadData();
    }
  }

  ngOnInit(): void {
    this.patientIDAsNumber = Number(this.patientID());
    this.isLoadingGraph = true;
  }

  loadData() {
    this.loadDataForGraph(this.selectedTargets()[0]?.ID!);
  }

  onGraphTypeChange(event: any) {
    this.selectedGraphType = event.value;
    this.loadDataForGraph(this.selectedTargets()[0]?.ID!);
  }

  updateAvailableGraphTypes() {
    // set the available graph types based on the target type
    switch (this.selectedTargets()[0]?.TargetType) {
        case TargetType.DiscreteTrialTeaching:
        case TargetType.Echoic:
          this.graphTypes = [
            {
              text: '% Correct Responding',
              value: TargetGraphType.PercentageCorrectResponding,
            },
            {
              text: 'Prompt % Distribution',
              value: TargetGraphType.PromptPercentageDistribution,
            },
            { text: 'Prompt Counts', value: TargetGraphType.PromptCounts },
            {
              text: 'Trials by Prompts',
              value: TargetGraphType.TrialsByPrompts,
            },
            {
              text: 'Daily Prompt %',
              value: TargetGraphType.DailyPromptPercentage,
            },
            { text: 'Time of Day', value: TargetGraphType.TimeOfDay },
          ];
          if (this.selectedGraphType == null) {
            this.selectedGraphType =
              TargetGraphType.PercentageCorrectResponding;
          }
          break;
        case TargetType.Frequency:
          this.graphTypes = [
            {
              text: 'Frequency Simple',
              value: TargetGraphType.FrequencySimple,
            },
          ];
          this.selectedGraphType = TargetGraphType.FrequencySimple;
          break;
        case TargetType.Duration:
          this.graphTypes = [
            {
              text: 'Duration Sum',
              value: TargetGraphType.DurationSum,
            },
            {
              text: 'Duration Count',
              value: TargetGraphType.DurationCount,
            },
            {
              text: 'Duration % On Session (By Session)',
              value: TargetGraphType.DurationPercentageOnSession,
            },
            {
              text: 'Duration % On Goal (By Trial)',
              value: TargetGraphType.DurationPercentageOnGoal,
            },
            {
              text: 'Duration Trials - Time of Day',
              value: TargetGraphType.DurationTrialsTimeOfDay,
            },
            {
              text: 'Duration By Trial',
              value: TargetGraphType.DurationByTrial,
            },
          ];
          if (this.selectedGraphType == null) {
            this.selectedGraphType = TargetGraphType.DurationSum;
          }
          break;
        case TargetType.Quantity:
          this.graphTypes = [
            {
              text: 'Quantity Count',
              value: TargetGraphType.QuantityCount,
            },
            {
              text: 'Quantity Sum',
              value: TargetGraphType.QuantitySum,
            },
          ];
          if (this.selectedGraphType == null) {
            this.selectedGraphType = TargetGraphType.QuantitySum;
          }
          break;
        case TargetType.FluencyRate:
          this.graphTypes = [
            {
              text: 'Fluency Average',
              value: TargetGraphType.FluencyAverage,
            },
            {
              text: 'Fluency Individual',
              value: TargetGraphType.FluencyIndividual,
            },
            {
              text: '% Correct with Elapsed Time',
              value: TargetGraphType.FluencyPercentCorrectWithElapsedTime,
            },
          ];
          if (this.selectedGraphType == null) {
            this.selectedGraphType = TargetGraphType.FluencyAverage;
          }
          break;
        case TargetType.TaskAnalysis:
          this.graphTypes = [
            {
              text: 'Task Analysis Line',
              value: TargetGraphType.TaskAnalysisLine,
            },
            {
              text: 'Task Analysis Bar Simple',
              value: TargetGraphType.TaskAnalysisBarSimple,
            },
            {
              text: 'Task Analysis Bar Count',
              value: TargetGraphType.TaskAnalysisBarPromptCount,
            },
            {
              text: 'Task Analysis Bar Percentage',
              value: TargetGraphType.TaskAnalysisBarPromptPercentage,
            },
          ];
          if (this.selectedGraphType == null) {
            this.selectedGraphType = TargetGraphType.TaskAnalysisLine;
          }
          break;
        case TargetType.Toileting:
          this.graphTypes = [
            {
              text: 'Toileting Line Column',
              value: TargetGraphType.ToiletingLineColumn,
            },
            {
              text: 'Toileting Bar',
              value: TargetGraphType.ToiletingBar,
            },
            { text: 'Toileting Pie', value: TargetGraphType.ToiletingPie },
            {
              text: 'Toileting % Correct',
              value: TargetGraphType.ToiletingPercentageCorrect,
            },
            {
              text: 'Accidents vs. Successes',
              value: TargetGraphType.ToiletingAccidentsVsSuccesses,
            },
          ];
          if (this.selectedGraphType == null) {
            this.selectedGraphType = TargetGraphType.ToiletingLineColumn;
          }
          break;
        case TargetType.WholePartialInterval:
          this.graphTypes = [
            {
              text: 'Whole Partial Interval Correct',
              value: TargetGraphType.WholePartialIntervalCorrect,
            },
            {
              text: 'Whole/Partial Interval % Not Happened',
              value: TargetGraphType.WholePartialIntervalNotHappened,
            },
          ];
          if (
            this.selectedGraphType !==
              TargetGraphType.WholePartialIntervalCorrect &&
            this.selectedGraphType !==
              TargetGraphType.WholePartialIntervalNotHappened
          ) {
            this.selectedGraphType =
              TargetGraphType.WholePartialIntervalCorrect;
          }
          break;
        case TargetType.Currency:
          // Single graph for currency � use Currency graph type from backend, render as running balance
          this.graphTypes = [];
          this.selectedGraphType = TargetGraphType.Currency;
          break;
        case TargetType.Anecdotal:
          this.graphTypes = [];
          this.selectedGraphType = TargetGraphType.Anecdotal;
          break;
        default:
          this.graphTypes = [];
          break;
    }
  }

  hasSupervisorPermission(): boolean {
    const userInfo = this.authService.userInfo;
    if (!userInfo || !userInfo.Permissions) {
      return false;
    }

    return userInfo.Permissions.includes(Permissions.SparkApp_Supervisor);
  }

  loadDataForGraph(targetID: number) {
    this.updateAvailableGraphTypes();

    var startTime = this.startDate();
    var endTime = this.endDate();

    var graphType = this.selectedGraphType;
    if (this.selectedTargets()[0]?.TargetType == TargetType.Currency) {
      graphType = TargetGraphType.Currency;
    } else if (this.selectedTargets()[0]?.TargetType == TargetType.Anecdotal) {
      graphType = TargetGraphType.Anecdotal;
    } else if (this.selectedTargets()[0]?.TargetType == TargetType.GroupedFrequency) {
      graphType = TargetGraphType.GroupedFrequency;
    } else if (
      this.selectedTargets()[0]?.TargetType ==
      TargetType.ConditionalDiscrimination
    ) {
      graphType = TargetGraphType.ConditionalDescrimination;
    }

    // set targetIDs to an array of all targetIDs
    const targetIDs = this.selectedTargets()
      .map((target) => target.ID!)
      .filter((id) => id !== null);

    this.isLoadingGraph = true;

    this.loadGraphData(targetIDs, startTime, endTime, graphType!);
  }

  private loadGraphData(
    targetIDs: number[],
    startTime: Date,
    endTime: Date,
    graphType: TargetGraphType
  ) {
    this.graphApiService
      .GetTargetGraph({
        patientID: Number(this.patientID()),
        targetIDs: targetIDs,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        graphType: graphType!,
        graphGrouping: graphType === TargetGraphType.DurationByTrial ? GraphGrouping.ByTrial : this.groupingOption,
        staffID: this.selectedStaffMember?.StaffID,
        includeTrendLine: this.displayTrendLine(),
        segmentTrendLineByPhase: this.displayTrendLine() && this.segmentTrendLineByPhase(),
        mergeSimilarPrompts: this.mergeSimilarPrompts(),
        showNotTrackedAsZeroes:
          this.shouldDisplayShowNotTrackedAsZeroes() &&
          this.showNotTrackedAsZeroes(),
      })
      .subscribe(
        (data) => {
          console.log(data);

          console.log('### loadGraphData data received - ' + new Date().toISOString());

          this.staffMembers.set(data.DataTakers ?? []);

          // do the correction only for the necessary graph types
          switch (graphType) {
            case TargetGraphType.PercentageCorrectResponding:
            case TargetGraphType.TrialsByPrompts:
            case TargetGraphType.DailyPromptPercentage:
            case TargetGraphType.TimeOfDay:
            case TargetGraphType.GroupedFrequency:
            case TargetGraphType.TaskAnalysisLine:
            case TargetGraphType.ConditionalDescrimination:
            case TargetGraphType.CombinationSeparated:
            case TargetGraphType.CombinationMerged:
            case TargetGraphType.ToiletingLineColumn:
            case TargetGraphType.ToiletingPercentageCorrect:
            case TargetGraphType.ToiletingAccidentsVsSuccesses:
      case TargetGraphType.FrequencySimple:
      case TargetGraphType.FluencyAverage:
      case TargetGraphType.FluencyIndividual:
      case TargetGraphType.WholePartialIntervalCorrect:
      case TargetGraphType.WholePartialIntervalNotHappened:
      case TargetGraphType.DurationSum:
      case TargetGraphType.DurationCount:
      case TargetGraphType.DurationPercentageOnGoal:
      case TargetGraphType.DurationPercentageOnSession:
      case TargetGraphType.QuantitySum:
      case TargetGraphType.QuantityCount:
      case TargetGraphType.FluencyPercentCorrectWithElapsedTime:
      case TargetGraphType.DurationTrialsTimeOfDay:
      case TargetGraphType.DurationByTrial:
      case TargetGraphType.Currency:
      case TargetGraphType.Anecdotal:
              // Add placeholder data points for the start and end of the selected period
              if (this.forceGraphPeriod()) {
                const startDate = this.startDate().toISOString();
                const endDate = this.endDate().toISOString();

                if (data.Series && data.Series.length > 0) {
                  for (const series of data.Series) {
                    if (series.DataPoints && series.DataPoints.length > 0) {
                      const firstDataPoint = series.DataPoints[0];
                      const lastDataPoint =
                        series.DataPoints[series.DataPoints.length - 1];

                      if (firstDataPoint.Date !== startDate) {
                        series.DataPoints.unshift({
                          Date: startDate,
                          Value: null,
                        });
                      }

                      if (lastDataPoint.Date !== endDate) {
                        series.DataPoints.push({ Date: endDate, Value: null });
                      }
                    } else {
                      // If no points in series, add placeholders for the entire period
                      series.DataPoints = [
                        { Date: startDate, Value: null },
                        { Date: endDate, Value: null },
                      ];
                    }
                  }
                }
              }
              break;
            default:
              break;
          }

          this.graphData.set(data);
          this.annotations.set(data.Annotations ?? []);
          this.updateAnnotationDisplayGroupCheckmarks();
          this.updateGraph();
          this.isLoadingGraph = false;
        },
        (error) => {
          console.error('Error fetching data', error);
          this.alertService.error(
            Utils.getErrorMessageFromErrorResponse(error)
          );
          this.graphData.set(null);
          this.updateGraph();
          this.isLoadingGraph = false;
        }
      );
  }

  shouldDisplayGraphTypeOptions(): boolean {
    return (
      this.selectedTargets()[0]?.TargetType ===
        TargetType.DiscreteTrialTeaching ||
      this.selectedTargets()[0]?.TargetType === TargetType.Echoic ||
      this.selectedTargets()[0]?.TargetType === TargetType.Duration ||
      this.selectedTargets()[0]?.TargetType === TargetType.FluencyRate ||
      this.selectedTargets()[0]?.TargetType === TargetType.Quantity ||
      this.selectedTargets()[0]?.TargetType === TargetType.TaskAnalysis ||
      this.selectedTargets()[0]?.TargetType === TargetType.Toileting ||
      this.selectedTargets()[0]?.TargetType ===
        TargetType.WholePartialInterval
    );
  }

  shouldDisplayAddAnnotationButton(): boolean {
    return this.hasSupervisorPermission();
  }

  shouldDisplayDataPointNumbersOption(): boolean {
    return (
      this.selectedGraphType === TargetGraphType.PercentageCorrectResponding ||
      this.selectedGraphType === TargetGraphType.TrialsByPrompts ||
      this.selectedGraphType === TargetGraphType.DailyPromptPercentage ||
      this.selectedGraphType === TargetGraphType.FrequencySimple ||
      this.selectedGraphType === TargetGraphType.DurationSum ||
      this.selectedGraphType === TargetGraphType.DurationCount ||
      this.selectedGraphType === TargetGraphType.DurationByTrial ||
      this.selectedGraphType === TargetGraphType.QuantitySum ||
      this.selectedGraphType === TargetGraphType.QuantityCount ||
      this.selectedGraphType === TargetGraphType.FluencyAverage ||
      this.selectedGraphType === TargetGraphType.FluencyIndividual ||
      this.selectedGraphType === TargetGraphType.FluencyPercentCorrectWithElapsedTime ||
      this.selectedGraphType === TargetGraphType.GroupedFrequency ||
      this.selectedGraphType === TargetGraphType.TaskAnalysisLine ||
      this.selectedGraphType === TargetGraphType.CombinationSeparated ||
      this.selectedGraphType === TargetGraphType.CombinationMerged ||
      this.selectedGraphType === TargetGraphType.DurationPercentageOnGoal ||
      this.selectedGraphType === TargetGraphType.DurationPercentageOnSession ||
      this.selectedGraphType === TargetGraphType.ToiletingPercentageCorrect ||
      this.selectedGraphType === TargetGraphType.ToiletingAccidentsVsSuccesses ||
      this.selectedGraphType === TargetGraphType.WholePartialIntervalCorrect ||
      this.selectedGraphType ===
        TargetGraphType.WholePartialIntervalNotHappened ||
      this.selectedGraphType === TargetGraphType.Anecdotal ||
      this.selectedGraphType === TargetGraphType.ToiletingLineColumn
    );
  }

  shouldDisplayTrendLineOption(): boolean {
    return (
      this.selectedGraphType === TargetGraphType.FrequencySimple ||
      this.selectedGraphType === TargetGraphType.DurationSum ||
      this.selectedGraphType === TargetGraphType.DurationCount ||
      this.selectedGraphType === TargetGraphType.DurationByTrial ||
      this.selectedGraphType === TargetGraphType.DurationPercentageOnGoal ||
      this.selectedGraphType === TargetGraphType.DurationPercentageOnSession ||
      this.selectedGraphType === TargetGraphType.QuantitySum ||
      this.selectedGraphType === TargetGraphType.QuantityCount ||
      this.selectedGraphType === TargetGraphType.PercentageCorrectResponding ||
      this.selectedGraphType === TargetGraphType.CombinationSeparated ||
      this.selectedGraphType === TargetGraphType.CombinationMerged ||
      this.selectedGraphType === TargetGraphType.ToiletingPercentageCorrect ||
      this.selectedGraphType === TargetGraphType.ToiletingAccidentsVsSuccesses ||
      this.selectedGraphType === TargetGraphType.WholePartialIntervalCorrect ||
      this.selectedGraphType ===
        TargetGraphType.WholePartialIntervalNotHappened ||
      this.selectedGraphType === TargetGraphType.Anecdotal ||
      this.selectedGraphType === TargetGraphType.FluencyAverage ||
      this.selectedGraphType === TargetGraphType.FluencyIndividual ||
      this.selectedGraphType === TargetGraphType.FluencyPercentCorrectWithElapsedTime
    );
  }

  shouldDisplayToggleGrouped(): boolean {
    return (
      this.selectedTargets()[0]?.TargetType == TargetType.GroupedFrequency ||
      this.selectedTargets()[0]?.TargetType == TargetType.TaskAnalysis ||
      this.selectedTargets()[0]?.TargetType ==
        TargetType.ConditionalDiscrimination
    );
  }

  shouldDisplayGroupingOptions(): boolean {
    return (
      this.selectedGraphType === TargetGraphType.DurationCount ||
      this.selectedGraphType === TargetGraphType.DurationSum ||
      this.selectedGraphType === TargetGraphType.FrequencySimple ||
      this.selectedGraphType === TargetGraphType.PercentageCorrectResponding ||
      this.selectedGraphType === TargetGraphType.TrialsByPrompts ||
      this.selectedGraphType === TargetGraphType.QuantityCount ||
      this.selectedGraphType === TargetGraphType.QuantitySum ||
      this.selectedGraphType === TargetGraphType.FluencyAverage ||
      this.selectedGraphType === TargetGraphType.FluencyIndividual ||
      this.selectedGraphType === TargetGraphType.FluencyPercentCorrectWithElapsedTime ||
      this.selectedGraphType === TargetGraphType.GroupedFrequency ||
      this.selectedGraphType === TargetGraphType.TaskAnalysisLine ||
      this.selectedGraphType === TargetGraphType.ConditionalDescrimination ||
      this.selectedGraphType === TargetGraphType.CombinationSeparated ||
      this.selectedGraphType === TargetGraphType.CombinationMerged ||
      this.selectedGraphType === TargetGraphType.ToiletingLineColumn ||
      this.selectedGraphType === TargetGraphType.ToiletingPercentageCorrect ||
      this.selectedGraphType === TargetGraphType.ToiletingAccidentsVsSuccesses ||
      this.selectedGraphType === TargetGraphType.WholePartialIntervalCorrect ||
      this.selectedGraphType ===
        TargetGraphType.WholePartialIntervalNotHappened ||
      this.selectedGraphType === TargetGraphType.Currency ||
      this.selectedGraphType === TargetGraphType.Anecdotal
    );
  }

  shouldDisplayStaffMemberOptions(): boolean {
    return (
      this.staffMembers().length > 1 &&
      this.groupingOption != GraphGrouping.ByStaffMember
    );
  }

  shouldDisplayGraph(): boolean {
    return (
      this.isLoadingGraph === false &&
      this.selectedTargets().length > 0 &&
      !this.isGraphEmpty()
    );
  }

  shouldDisplayDisplayOptions(): boolean {
    return (
      this.selectedGraphType === TargetGraphType.FrequencySimple ||
      this.selectedGraphType === TargetGraphType.PercentageCorrectResponding ||
      this.selectedGraphType === TargetGraphType.PromptPercentageDistribution ||
      this.selectedGraphType === TargetGraphType.PromptCounts ||
      this.selectedGraphType === TargetGraphType.TrialsByPrompts ||
      this.selectedGraphType === TargetGraphType.DailyPromptPercentage ||
      this.selectedGraphType === TargetGraphType.TimeOfDay ||
      this.selectedGraphType === TargetGraphType.DurationSum ||
      this.selectedGraphType === TargetGraphType.DurationCount ||
      this.selectedGraphType === TargetGraphType.DurationByTrial ||
      this.selectedGraphType === TargetGraphType.DurationPercentageOnGoal ||
      this.selectedGraphType === TargetGraphType.DurationPercentageOnSession ||
      this.selectedGraphType === TargetGraphType.QuantitySum ||
      this.selectedGraphType === TargetGraphType.QuantityCount ||
      this.selectedGraphType === TargetGraphType.FluencyAverage ||
      this.selectedGraphType === TargetGraphType.FluencyIndividual ||
      this.selectedGraphType === TargetGraphType.FluencyPercentCorrectWithElapsedTime ||
      this.selectedGraphType === TargetGraphType.GroupedFrequency ||
      this.selectedGraphType === TargetGraphType.TaskAnalysisLine ||
      this.selectedGraphType === TargetGraphType.ConditionalDescrimination ||
      this.selectedGraphType === TargetGraphType.CombinationSeparated ||
      this.selectedGraphType === TargetGraphType.CombinationMerged ||
      this.selectedGraphType === TargetGraphType.ToiletingPercentageCorrect ||
      this.selectedGraphType === TargetGraphType.ToiletingAccidentsVsSuccesses ||
      this.selectedGraphType === TargetGraphType.WholePartialIntervalCorrect ||
      this.selectedGraphType ===
        TargetGraphType.WholePartialIntervalNotHappened ||
      this.selectedGraphType === TargetGraphType.Anecdotal
    );
  }

  shouldDisplayMergeSimilarPromptsOptions(): boolean {
    return (
      this.selectedGraphType === TargetGraphType.PromptPercentageDistribution ||
      this.selectedGraphType === TargetGraphType.PromptCounts ||
      this.selectedGraphType === TargetGraphType.TrialsByPrompts ||
      this.selectedGraphType === TargetGraphType.DailyPromptPercentage
    );
  }

  shouldDisplayShowNotTrackedAsZeroes(): boolean {
    return (
      this.selectedGraphType === TargetGraphType.FrequencySimple ||
      this.selectedGraphType === TargetGraphType.WholePartialIntervalCorrect ||
      this.selectedGraphType ===
        TargetGraphType.WholePartialIntervalNotHappened
    );
  }

  shouldDisplayLegendOption(): boolean {
    return (
      this.selectedGraphType === TargetGraphType.DailyPromptPercentage ||
      this.selectedGraphType === TargetGraphType.TrialsByPrompts
    );
  }

  shouldDisplayAnnotationsOption(): boolean {
    return (
      this.selectedGraphType === TargetGraphType.PercentageCorrectResponding ||
      this.selectedGraphType === TargetGraphType.TrialsByPrompts ||
      this.selectedGraphType === TargetGraphType.DailyPromptPercentage ||
      this.selectedGraphType === TargetGraphType.ToiletingPercentageCorrect ||
      this.selectedGraphType === TargetGraphType.ToiletingAccidentsVsSuccesses ||
      this.selectedGraphType === TargetGraphType.WholePartialIntervalCorrect ||
      this.selectedGraphType ===
        TargetGraphType.WholePartialIntervalNotHappened ||
      this.selectedGraphType === TargetGraphType.Anecdotal
    );
  }

  isGraphEmpty(): boolean {
    // check if the graph data is empty
    if (this.graphData() == null) {
      return true;
    }

    // check if series is empty
    if (this.graphData()?.Series?.length == 0) {
      return true;
    }

    // for some graph types, check the lenght of the first series as well.
    switch (this.selectedGraphType) {
      case TargetGraphType.PercentageCorrectResponding:
      case TargetGraphType.TimeOfDay:
      case TargetGraphType.FrequencySimple:
      case TargetGraphType.DurationCount:
      case TargetGraphType.DurationSum:
      case TargetGraphType.DurationByTrial:
      case TargetGraphType.QuantitySum:
      case TargetGraphType.QuantityCount:
      case TargetGraphType.FluencyAverage:
      case TargetGraphType.FluencyIndividual:
      case TargetGraphType.FluencyPercentCorrectWithElapsedTime:
      case TargetGraphType.ToiletingAccidentsVsSuccesses:
        return this.graphData()!.Series![0].DataPoints!.length == 0;

      default:
        return false;
    }
  }

  formatDateForInput(date: Date): string {
    return Utils.formatDateForInput(date);
  }

  onStartDateChange(event: any) {
    if (!event) return;

    // Clear any existing timer
    if (this.dateChangeTimer) {
      clearTimeout(this.dateChangeTimer);
    }

    // Set new timer
    this.dateChangeTimer = setTimeout(() => {
      const date = Utils.parseDateFromInput(event);
      this.startDate.set(date);
      this.dateOption = this.dateOptions[0].value; // Custom option value
      this.reloadGraphForDateChange();
    }, 1500); // Wait 1.5 seconds after last change
  }

  onEndDateChange(event: any) {
    if (!event) return;

    // Clear any existing timer
    if (this.dateChangeTimer) {
      clearTimeout(this.dateChangeTimer);
    }

    // Set new timer
    this.dateChangeTimer = setTimeout(() => {
      const date = Utils.parseDateFromInput(event);
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);
      this.endDate.set(endOfDay);
      this.dateOption = this.dateOptions[0].value; // Custom option value
      this.reloadGraphForDateChange();
    }, 1500); // Wait 1.5 seconds after last change
  }

  onDateChange(event: any) {
    const selectedDate = event.target.valueAsDate;
    // date is coming in UTC, we need to convert it to local time
    const localDate = new Date(
      selectedDate.getTime() + selectedDate.getTimezoneOffset() * 60000
    );
    const localDateEndOfDay = new Date(
      localDate.getTime() +
        23 * 60 * 60 * 1000 +
        59 * 60 * 1000 +
        59 * 1000 +
        999
    );

    this.startDate.set(localDate);
    this.endDate.set(localDateEndOfDay);
    //this.loadDataForGraph(this.selectedTargets()[0]!.ID!);
  }

  onToggleGrouped() {
    this.updateGraph();
  }

  onDateOptionChange(event: any) {
    // Don't update if custom is selected
    if (event.value === this.dateOptions[0].value) {
      return;
    }

    // Update start date based on the selected option
    this.startDate.set(event.value);

    // Set end date to end of day today
    const endOfDayToday = new Date(
      new Date().getTime() +
        23 * 60 * 60 * 1000 +
        59 * 60 * 1000 +
        59 * 1000 +
        999
    );
    this.endDate.set(endOfDayToday);

    this.reloadGraphForDateChange();
  }

  private reloadGraphForDateChange(): void {
    if (this.selectedTargets().length > 0) {
      this.loadDataForGraph(this.selectedTargets()[0]!.ID!);
    }
  }

  onGroupingOptionChange(event: any) {
    this.groupingOption = event.value;

    if (this.groupingOption == GraphGrouping.ByStaffMember) {
      this.selectedStaffMember = null;
    }

    this.loadDataForGraph(this.selectedTargets()[0]!.ID!);
  }

  onForceGraphPeriodChange(checked: boolean) {
    this.forceGraphPeriod.set(checked);
    if (this.selectedTargets().length > 0) {
      this.loadDataForGraph(this.selectedTargets()[0]!.ID!);
    }
  }

  onStaffMemberChange(event: any) {
    this.selectedStaffMember = event.value;
    this.loadDataForGraph(this.selectedTargets()[0]!.ID!);
  }

  onSeriesDataLabelOptionChange() {
    this.updateGraph();
  }

  onTrendLineDataLabelOptionChange() {
    this.updateGraph();
  }

  override getDataLabelsConfig(): any {
    const seriesOption = this.seriesDataLabelOption();
    const trendLineOption = this.trendLineDataLabelOption();
    const seriesCount = this.graphData()?.Series?.length ?? 0;
    const hasTrendLine =
      this.displayTrendLine() && seriesCount > 0 && seriesCount <= 2;

    return this.buildSeriesTrendDataLabelsConfig(seriesOption, trendLineOption, {
      hasTrendLine,
      seriesCount: Math.max(seriesCount, 1),
      alternateTrendLines: false,
    });
  }

  getEnabledOnSeries(
    seriesOption: SeriesDataLabelOption,
    trendLineOption: TrendLineDataLabelOption
  ): number[] {
    const enabled: number[] = [];

    // Series (index 0) - enabled if not LineOnly
    if (seriesOption !== SeriesDataLabelOption.LineOnly) {
      enabled.push(0);
    }

    // Trend line (index 1) - enabled if not LineOnly and trend line is displayed
    if (
      this.displayTrendLine() &&
      trendLineOption !== TrendLineDataLabelOption.LineOnly
    ) {
      enabled.push(1);
    }

    return enabled;
  }

  override getMarkersConfig(): ApexMarkers {
    const seriesOption = this.seriesDataLabelOption();
    const trendLineOption = this.trendLineDataLabelOption();
    const series = this.graphData()?.Series ?? [];
    const seriesCount = series.length;
    const hasTrendLine =
      this.displayTrendLine() && seriesCount > 0 && seriesCount <= 2;
    const graphData = this.graphData();
    const seriesDataList = graphData
      ? series.map((s) => this.processSeriesDataWithBreaks(s, graphData).data)
      : [];
    const colors = this.resolveChartColors(series);

    return this.buildSeriesTrendMarkersConfig(seriesOption, trendLineOption, {
      hasTrendLine,
      seriesCount: Math.max(seriesCount, 1),
      alternateTrendLines: false,
      seriesDataList,
      colors,
    });
  }

  shouldDisplayAnnotationLabelBackgroundOption(): boolean {
    return this.shouldDisplayAnnotationsOption();
  }

  onAnnotationLabelBackgroundOptionSelected(value: AnnotationLabelBackgroundOption): void {
    this.annotationLabelBackgroundOption.set(value);
    this.onAnnotationLabelBackgroundOptionChange();
  }

  onDeclutterAnnotationsSelected(value: boolean): void {
    this.declutterAnnotations.set(value);
    this.onDeclutterAnnotationsChange();
  }

  onAnnotationLabelPositionChange(
    annotation: GraphAnnotationModel,
    labelPosition: 'start' | 'middle' | 'end'
  ): void {
    this.applyAnnotationLabelPositionChange(
      annotation,
      labelPosition,
      this.graphData(),
      (value) => this.graphData.set(value),
      (updated) => {
        this.graphApiService.SaveGraphAnnotation({ body: updated }).subscribe({
          next: () => this.refreshChartAnnotations(),
          error: (error) =>
            this.alertService.error(
              'Error saving annotation - ' + Utils.getErrorMessageFromErrorResponse(error)
            ),
        });
      }
    );
  }

  protected override resolveDeclutterAnnotationCandidates(
    graphData: GraphDataModel | null | undefined
  ): GraphAnnotationModel[] {
    if (!graphData?.Annotations?.length) {
      return [];
    }

    if (this.isGrouped() && (graphData.Series?.length ?? 0) > 0) {
      const seen = new Set<string>();
      const candidates: GraphAnnotationModel[] = [];
      for (const series of graphData.Series ?? []) {
        for (const annotation of this.getAnnotationsShownOnGraph(graphData, series)) {
          const key = this.annotationDisplayKey(annotation);
          if (seen.has(key)) {
            continue;
          }
          seen.add(key);
          candidates.push(annotation);
        }
      }
      return candidates;
    }

    return this.getAnnotationsShownOnGraph(graphData, graphData.Series?.[0] ?? null);
  }

  getAnnotationListItems() {
    return this.getVisibleAnnotationListItems(this.startDate(), this.endDate());
  }

  onDisplayLegendChange() {
    this.updateGraph();
  }

  onToggleMergeSimilarPrompts() {
    this.loadDataForGraph(this.selectedTargets()[0]!.ID!);
  }

  onToggleShowNotTrackedAsZeroes() {
    this.loadDataForGraph(this.selectedTargets()[0]!.ID!);
  }

  override updateGraph() {
    console.log('updateGraph start - ' + new Date().toISOString());
    if (!this.graphData()) {
      // hide graph
      this.chartOptions.series = [];
      return;
    }
    
    switch (this.selectedGraphType) {
      case TargetGraphType.PercentageCorrectResponding:
        this.loadPercentageCorrectRespondingGraphOptions();
        break;
      case TargetGraphType.PromptPercentageDistribution:
        this.loadPromptPercentageDistributionGraphOptions();
        break;
      case TargetGraphType.PromptCounts:
        this.loadPromptCountsGraphOptions();
        break;
      case TargetGraphType.TrialsByPrompts:
        this.loadTrialsByPromptsGraphOptions();
        break;
      case TargetGraphType.DailyPromptPercentage:
        this.loadDailyPromptPercentageGraphOptions();
        break;
      case TargetGraphType.TimeOfDay:
        this.loadTimeOfDayGraphOptions(
          this.graphData()!,
          this.shouldRenderAnnotations(),
          this.staffMembers()
        );
        break;
      case TargetGraphType.FrequencySimple:
        this.loadFrequencySimpleGraphOptions();
        break;
      case TargetGraphType.DurationSum:
      case TargetGraphType.DurationByTrial:
        this.loadDefaultLineGraphOptions(
          this.graphData()!,
          this.seriesDataLabelOption(),
          this.displayLegend(),
          this.shouldRenderAnnotations(),
          false,
          false,
          false,
          false,
          this.staffMembers()
        );
        break;
      case TargetGraphType.DurationCount:
        this.loadDurationCountGraphOptions();
        break;
      case TargetGraphType.DurationPercentageOnSession:
        this.loadDefaultLineGraphOptions(
          this.graphData()!,
          this.seriesDataLabelOption(),
          this.displayLegend(),
          this.shouldRenderAnnotations(),
          false,
          true,
          true
        );
        break;
      case TargetGraphType.DurationPercentageOnGoal:
        this.loadDurationPercentageOnGoalGraphOptions();
        break;
      case TargetGraphType.QuantitySum:
      case TargetGraphType.QuantityCount:
        this.loadQuantityLineGraphOptions();
        break;
      case TargetGraphType.FluencyAverage:
      case TargetGraphType.FluencyIndividual:
      case TargetGraphType.FluencyPercentCorrectWithElapsedTime:
        this.loadFluencyLineGraphOptions();
        break;
      case TargetGraphType.GroupedFrequency:
        this.loadDefaultLineGraphOptions(
          this.graphData()!,
          this.seriesDataLabelOption(),
          this.displayLegend(),
          this.shouldRenderAnnotations()
        );
        break;
      case TargetGraphType.ConditionalDescrimination:
        this.loadDefaultLineGraphOptions(
          this.graphData()!,
          this.seriesDataLabelOption(),
          this.displayLegend(),
          this.shouldRenderAnnotations(),
          this.displayTrendLine(),
          true,
          false,
          false,
          this.staffMembers()
        );
        break;
      case TargetGraphType.TaskAnalysisLine:
        this.loadDefaultLineGraphOptions(
          this.graphData()!,
          this.seriesDataLabelOption(),
          this.displayLegend(),
          this.shouldRenderAnnotations(),
          this.displayTrendLine(),
          true,
          false,
          true,
          this.staffMembers()
        );
        break;
      case TargetGraphType.CombinationSeparated:
      case TargetGraphType.CombinationMerged:
        // if all selected targets are prompt based, set ylimits to 0-100
        let shouldSetYLimits = false;
        if (
          this.selectedTargets() &&
          this.selectedTargets().every(
            (target) =>
              target.TargetType === TargetType.DiscreteTrialTeaching ||
              target.TargetType === TargetType.Echoic
          )
        ) {
          shouldSetYLimits = true;
        }
        this.loadDefaultLineGraphOptions(
          this.graphData()!,
          this.seriesDataLabelOption(),
          this.displayLegend(),
          this.shouldRenderAnnotations(),
          this.displayTrendLine(),
          shouldSetYLimits,
          false,
          this.isGrouped()
        );
        break;
      case TargetGraphType.TaskAnalysisBarSimple:
        this.loadTaskAnalysisBarSimpleGraphOptions();
        break;
      case TargetGraphType.TaskAnalysisBarPromptCount:
        this.loadTaskAnalysisBarCountGraphOptions();
        break;
      case TargetGraphType.TaskAnalysisBarPromptPercentage:
        this.loadTaskAnalysisBarPercentageGraphOptions();
        break;
      case TargetGraphType.ToiletingLineColumn:
        this.loadToiletingLineColumnGraphOptions();
        break;
      case TargetGraphType.ToiletingBar:
        this.loadToiletingBarGraphOptions();
        break;
      case TargetGraphType.ToiletingPie:
        this.loadToiletingPieGraphOptions();
        break;
      case TargetGraphType.ToiletingPercentageCorrect:
        this.loadToiletingPercentageCorrectGraphOptions();
        break;
      case TargetGraphType.ToiletingAccidentsVsSuccesses:
        this.loadDefaultLineGraphOptions(
          this.graphData()!,
          this.seriesDataLabelOption(),
          this.displayLegend(),
          this.shouldRenderAnnotations(),
          this.displayTrendLine(),
          false,
          false,
          false,
          this.staffMembers()
        );
        break;
      case TargetGraphType.WholePartialIntervalCorrect:
      case TargetGraphType.WholePartialIntervalNotHappened:
        this.loadWholePartialIntervalGraphOptions();
        break;
      case TargetGraphType.DurationTrialsTimeOfDay:
        this.loadDurationByTrialGraphOptions();
        break;
      case TargetGraphType.Currency:
        this.loadCurrencyLineGraphOptions();
        break;
      case TargetGraphType.Anecdotal:
        this.loadAnecdotalGraphOptions();
        break;
      default:
        return;
    }

    // Set a default filename immediately to prevent random filename generation
    this.setDefaultExportFilename();

    // we need to call this so that the options are actually updated on the graph
    this.applyChartOptions(this.chart, this.chartOptions);
    if (!this.isGrouped() && this.chartList?.length) {
      this.chartList.forEach((syncedChart, index) => {
        const syncedOptions = this.chartsOptions[index];
        if (syncedOptions) {
          this.applyChartOptions(syncedChart, syncedOptions);
        }
      });
    }

    console.log('updateGraph end - ' + new Date().toISOString());
  }

  loadCurrencyLineGraphOptions() {
    const startingBalance = this.selectedTargets()[0]?.StartingBalance ?? 0;
    const graphData = this.graphData();
    if (!graphData || !graphData.Series || graphData.Series.length === 0) {
      return;
    }
    
    // Collect all balance values from the first series (balance series)
    const balanceValues: number[] = [];
    const balanceSeries = graphData.Series[0];
    if (balanceSeries && balanceSeries.DataPoints) {
      balanceSeries.DataPoints.forEach((dp) => {
        if (dp.Value !== null && dp.Value !== undefined) {
          balanceValues.push(dp.Value);
        }
      });
    }
    
    // Include starting balance in the range calculation
    balanceValues.push(startingBalance);
    
    // Calculate min and max, ensuring starting balance is included
    const minValue = balanceValues.length > 0 ? Math.min(...balanceValues) : startingBalance;
    const maxValue = balanceValues.length > 0 ? Math.max(...balanceValues) : startingBalance;
    
    // Add some padding (5% on each side) for better visualization
    const range = maxValue - minValue;
    const padding = range * 0.05 || Math.abs(startingBalance) * 0.05 || 1;
    const yMin = minValue - padding;
    const yMax = maxValue + padding;
    
    this.chartOptions = {
      series: graphData.Series.map((series, index) => ({
        name: series.Name ?? 'No Name',
        type: index == 0 ? 'column' : 'line',
        color: series.Color ?? undefined,
        data: series.DataPoints!.map((data) => data.Value!),
      })),
      chart: {
        height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
        width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
        type: 'line',
      },
      stroke: {
        width: [4, 2],
      },
      title: {
        text: graphData.Title ?? 'Chart',
      },
      dataLabels: this.getDataLabelsConfig(),
      // get the first series, x should be in this format: 01 Jan 2001
      labels: graphData.Series[0]?.DataPoints?.map((data) =>
        data.Date
          ? new Date(data.Date).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : ''
      ) ?? [],
      xaxis: {
        type: 'datetime',
      },
      yaxis: [
        {
          min: yMin,
          max: yMax,
          title: {
            text: 'Balance',
          },
        },
        {
          opposite: true,
          title: {
            text: 'Occurences',
          },
        },
      ],
      tooltip: {
        custom: this.createCustomTooltipCurrency(balanceSeries.DataPoints!.map((data) => ({
          timestamp: data.Date ? new Date(data.Date).getTime() : new Date().getTime(),
          delta: data.Value ?? null,
          balance: data.Value ?? null,
          staffIDs: data.StaffIDs ?? [],
        })), this.staffMembers()),
      },
      annotations: this.buildAnnotationsConfig(
        graphData,
        null,
        this.shouldRenderAnnotations()
      ),
    };
  }

  createCustomTooltipCurrency(
    tooltipData: {
      timestamp: number;
      delta: number | null;
      balance: number | null;
      staffIDs: number[];
    }[],
    staffMembers: DataTakerStaffDataModel[]
  ) {
    const self = this;
    return function ({
      series,
      seriesIndex,
      dataPointIndex,
      w,
    }: {
      series: any[];
      seriesIndex: number;
      dataPointIndex: number;
      w: any;
    }) {
      const point = tooltipData[dataPointIndex];
      if (!point) return null;

      const dateToDisplay = new Date(
        point.timestamp ?? new Date().getTime()
      ).toDateString();
      const seriesColor =
        w.globals.colors[seriesIndex % w.globals.colors.length] ?? '#008FFB';

      // Staff names
      const staffIDs = point.staffIDs;
      const staffsForDataPoint = staffMembers.filter((sm) =>
        staffIDs?.includes(sm.StaffID!)
      );
      const staffMembersToDisplay = staffsForDataPoint
        .map((sm) => sm.FullName || 'No User')
        .join(', ');
      const finalStaffDisplay = staffMembersToDisplay || 'No User';
      const staffMembersHTML =
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Staff Members:</span>' +
        '<span class="tooltip-value">' +
        finalStaffDisplay +
        '</span>' +
        '</div>';

      const dayDelta = point.delta;
      const running = point.balance;

      return (
        '<div class="tooltip-card">' +
        '<div class="tooltip-card-header">' +
        '<h5 class="tooltip-card-title" style="color: ' +
        seriesColor +
        ';">' +
        (w.globals.seriesNames?.[seriesIndex] ?? 'Series') +
        '<br>' +
        '(' +
        dateToDisplay +
        ')' +
        '</h5>' +
        '</div>' +
        '<div class="tooltip-card-body">' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Change:</span>' +
        '<span class="tooltip-value">' +
        (dayDelta !== null ? (dayDelta >= 0 ? '+' : '') + dayDelta : '-') +
        '</span>' +
        '</div>' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Balance:</span>' +
        '<span class="tooltip-value">' +
        (running !== null ? running : '-') +
        '</span>' +
        '</div>' +
        staffMembersHTML +
        '</div>' +
        '</div>'
      );
    };
  }
  private setDefaultExportFilename(): void {
    // Set a basic filename immediately to prevent ApexCharts from generating a random one
    const graphType = this.getGraphTypeDisplayName() || 'Graph';
    const targetName = this.selectedTargets()[0]?.TargetName || 'Target';
    const defaultFilename = `${graphType}-${targetName}`
      .replace(/[^a-zA-Z0-9\s-]/g, '')
      .replace(/\s+/g, '_')
      .substring(0, 100);

    if (this.chartOptions.chart?.toolbar?.export) {
      if (!this.chartOptions.chart.toolbar.export.csv) {
        this.chartOptions.chart.toolbar.export.csv = {};
      }
      if (!this.chartOptions.chart.toolbar.export.svg) {
        this.chartOptions.chart.toolbar.export.svg = {};
      }
      if (!this.chartOptions.chart.toolbar.export.png) {
        this.chartOptions.chart.toolbar.export.png = {};
      }
      this.chartOptions.chart.toolbar.export.csv.filename = defaultFilename;
      this.chartOptions.chart.toolbar.export.svg.filename = defaultFilename;
      this.chartOptions.chart.toolbar.export.png.filename = defaultFilename;
    }
  }

  loadToiletingLineColumnGraphOptions() {
    this.chartOptions = {
      series: this.graphData()!.Series!.map((series, index) => ({
        name: series.Name ?? 'No Name',
        type: index == 0 ? 'column' : 'line',
        color: series.Color ?? undefined,
        data: series.DataPoints!.map((data) => data.Value!),
      })),
      chart: {
        height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
        width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
        type: "line"
      },
      stroke: {
        width: [4, 2, 2, 2, 2, 2],
      },
      title: {
        text: this.graphData()?.Title ?? 'Chart',
      },
      dataLabels: this.getDataLabelsConfig(),
      // get the first series, x should be in this format: 01 Jan 2001
      labels: this.graphData()!.Series![0].DataPoints!.map((data) =>
        data.Date
          ? new Date(data.Date).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : ''
      ),
      xaxis: {
        type: 'datetime',
      },
      yaxis: [
        {
          title: {
            text: 'Duration (in minutes)',
          },
        },
        {
          opposite: true,
          title: {
            text: 'Occurences',
          },
        },
      ],
      tooltip: {
        enabled: true,
        x: {
          format: 'MMM dd yyyy',
        },
        y: {
          formatter(val, opts) {
            if (opts.seriesIndex == 0) {
              return val ? val.toFixed(2) + ' min' : '0 min';
            }
            return val ? val.toFixed(0) : '0';
          },
        },
        hideEmptySeries: true,
      },
      annotations: this.buildAnnotationsConfig(
        this.graphData()!,
        null,
        this.shouldRenderAnnotations()
      ),
    };
  }

  loadToiletingBarGraphOptions() {
    this.chartOptions = {
      title: {
        text: this.graphData()?.Title ?? 'Chart',
      },
      series: [
        {
          name: 'count',
          data:
            this.graphData()?.Series!.map((series) => series.Value ?? 0) ?? [],
        },
      ],
      chart: {
        height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
        width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
        type: 'bar',
        events: {
          click: function (chart, w, e) {
            // console.log(chart, w, e)
          },
        },
      },
      colors:
        this.graphData()?.Series!.map((series) => series.Color ?? '#008FFB') ??
        [],
      plotOptions: {
        bar: {
          columnWidth: '45%',
          distributed: true,
        },
      },
      dataLabels: {
        enabled: false,
      },
      legend: {
        show: false,
      },
      grid: {
        show: false,
      },
      xaxis: {
        categories:
          this.graphData()?.Series!.map((series) => series.Name ?? '') ?? [],
        labels: {
          style: {
            fontSize: '12px',
          },
          datetimeUTC: false,
        },
      },
    };
  }

  loadToiletingPieGraphOptions() {
    // Calculate responsive width based on available space
    const availableWidth = window.innerWidth;
    let chartWidth: number;

    if (availableWidth < 768) {
      // Mobile: use most of the width but leave some padding
      chartWidth = Math.min(availableWidth * 0.9, 400);
    } else if (availableWidth < 1200) {
      // Tablet: use a reasonable fixed size
      chartWidth = Math.min(availableWidth * 0.6, 500);
    } else {
      // Desktop: use a maximum reasonable size
      chartWidth = Math.min(availableWidth * 0.4, 600);
    }

    this.chartOptions = {
      title: {
        text: this.graphData()?.Title ?? 'Chart',
      },
      series:
        this.graphData()?.Series!.map((series) => series.Value ?? 0) ?? [],
      chart: {
        width: chartWidth,
        type: 'pie',
      },
      colors:
        this.graphData()?.Series!.map((series) => series.Color ?? '#008FFB') ??
        [],
      labels:
        this.graphData()?.Series!.map((series) => series.Name ?? '') ?? [],
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: {
              width: Math.min(window.innerWidth * 0.9, 400),
            },
            legend: {
              position: 'bottom',
            },
          },
        },
        {
          breakpoint: 480,
          options: {
            chart: {
              width: Math.min(window.innerWidth * 0.95, 350),
            },
            legend: {
              position: 'bottom',
            },
          },
        },
      ],
    };
  }

  loadToiletingPercentageCorrectGraphOptions() {
    this.chartOptions = {
      series: this.graphData()!.Series!.map((series) => ({
        name: series.Name ?? 'No Name',
        data: series.DataPoints!.map(
          (data) =>
            [
              data.Date ? new Date(data.Date).getTime() : new Date().getTime(),
              data.Value,
            ] as [number, number | null]
        ),
      })),
      legend: this.displayLegend()
        ? {
            position: 'top',
            onItemClick: {
              toggleDataSeries: true,
            },
            onItemHover: {
              highlightDataSeries: true,
            },
          }
        : {
            show: false,
          },
      chart: {
        height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
        width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
        type: 'line',
        ...this.sharedChartConfig,
      },
      dataLabels: this.getDataLabelsConfig(),
      stroke: {
        curve: 'straight',
      },
      grid: {
        padding: {
          right: 30,
          left: 20,
        },
      },
      markers: this.getMarkersConfig(),
      tooltip: {
        custom: this.createCustomTooltipToiletingPercentageCorrect(
          this.graphData()!,
          this.staffMembers()
        ),
      },
      title: {
        text: this.graphData()?.Title ?? 'Chart',
      },
      xaxis: {
        type: 'datetime',
        title: {
          text: this.graphData()?.XAxisTitle ?? 'X Axis',
        },
        labels: {
          datetimeUTC: false,
        },
      },
      yaxis: {
        min: 0,
        max: 100,
        title: {
          text: this.graphData()?.YAxisTitle ?? 'Y Axis',
        },
        labels: {
          formatter: (value: number) => {
            // If it's a whole number, don't show decimal places
            if (Number.isInteger(value)) {
              return value.toString();
            }
            // If it's a decimal, round to 2 decimal places
            return value.toFixed(2);
          },
        },
      },
      annotations: this.buildAnnotationsConfig(
        this.graphData()!,
        null,
        this.shouldRenderAnnotations()
      ),
    };
  }

  loadWholePartialIntervalGraphOptions() {
    this.chartOptions = {
      series: this.graphData()!.Series!.map((series) => ({
        name: series.Name ?? 'No Name',
        data: series.DataPoints!.map(
          (data) =>
            [
              data.Date ? new Date(data.Date).getTime() : new Date().getTime(),
              data.Value,
            ] as [number, number | null]
        ),
      })),
      legend: this.displayLegend()
        ? {
            position: 'top',
            onItemClick: {
              toggleDataSeries: true,
            },
            onItemHover: {
              highlightDataSeries: true,
            },
          }
        : {
            show: false,
          },
      chart: {
        height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
        width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
        type: 'line',
        ...this.sharedChartConfig,
      },
      dataLabels: this.getDataLabelsConfig(),
      stroke: {
        curve: 'straight',
      },
      grid: {
        padding: {
          right: 30,
          left: 20,
        },
      },
      markers: this.getMarkersConfig(),
      tooltip: {
        custom: this.createCustomTooltipWholePartialInterval(
          this.graphData()!,
          this.staffMembers()
        ),
      },
      title: {
        text: this.graphData()?.Title ?? 'Chart',
      },
      xaxis: {
        type: 'datetime',
        title: {
          text: this.graphData()?.XAxisTitle ?? 'X Axis',
        },
        labels: {
          datetimeUTC: false,
        },
      },
      yaxis: {
        min: 0,
        max: 100,
        title: {
          text: this.graphData()?.YAxisTitle ?? 'Y Axis',
        },
        labels: {
          formatter: (value: number) => {
            // If it's a whole number, don't show decimal places
            if (Number.isInteger(value)) {
              return value.toString();
            }
            // If it's a decimal, round to 2 decimal places
            return value.toFixed(2);
          },
        },
      },
      annotations: this.buildAnnotationsConfig(
        this.graphData()!,
        null,
        this.shouldRenderAnnotations()
      ),
    };
  }

  loadAnecdotalGraphOptions() {
    this.loadDefaultLineGraphOptions(
      this.graphData()!,
      this.seriesDataLabelOption(),
      this.displayLegend(),
      this.shouldRenderAnnotations(),
      this.displayTrendLine(),
      false,
      false,
      false,
      this.staffMembers()
    );

    this.chartOptions.tooltip = {
      custom: this.createCustomTooltipTextValue(
        this.graphData()!,
        this.staffMembers(),
        'Anecdotal Count',
        'Entries'
      ),
    };
  }

  createCustomTooltipToiletingPercentageCorrect(
    graphData: GraphDataModel,
    staffMembers: DataTakerStaffDataModel[]
  ) {
    return function ({
      series,
      seriesIndex,
      dataPointIndex,
      w,
    }: {
      series: any[];
      seriesIndex: number;
      dataPointIndex: number;
      w: any;
    }) {
      const dataPoint =
        graphData.Series![seriesIndex].DataPoints![dataPointIndex];
      const textToDisplay = dataPoint.TextValue ?? '';
      // the text is comma separated, so we need to split it and display each line in a new line
      const textLines = textToDisplay.split(',');

      // the order they come is Urination On, Urination Off, Bowel On, Bowel Off, No Void

      const staffIDs = dataPoint.StaffIDs;
      const staffsForDataPoint = staffMembers.filter((staffMember) =>
        staffIDs?.includes(staffMember.StaffID!)
      );
      const staffMembersToDisplay = staffsForDataPoint
        .map((staffMember) => staffMember.FullName || 'No User')
        .join(', ');

      const staffMembersHTML =
        staffMembersToDisplay.length > 0
          ? '<div class="tooltip-item">' +
            '<span class="tooltip-label">Staff Members:</span>' +
            '<span class="tooltip-value">' +
            staffMembersToDisplay +
            '</span>' +
            '</div>'
          : '';

      const dateToDisplay = new Date(
        dataPoint.Date ?? new Date().getTime()
      ).toDateString();
      const seriesName = graphData.Series![seriesIndex].Name ?? 'No Name';
      const seriesColor =
        w.globals.colors[seriesIndex % w.globals.colors.length] ?? '#008FFB';

      if (dataPoint.Value === null) {
        return null;
      }

      return (
        // we need to display them in the order of Urination On, Urination Off, Bowel On, Bowel Off, No Void
        // the color text for Urination Off and Bowel Off should be red
        // the color text for Urination On and Bowel On should be green
        // the color text for No Void should be gray
        '<div class="tooltip-card">' +
        '<div class="tooltip-card-header">' +
        '<h5 class="tooltip-card-title" style="color: ' +
        seriesColor +
        ';">' +
        seriesName +
        '(' +
        dateToDisplay +
        ')' +
        '</h5>' +
        '</div>' +
        '<div class="tooltip-card-body">' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Percentage Calculated:</span>' +
        '<span class="tooltip-value">' +
        dataPoint.Value +
        '%</span>' +
        '</div>' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label" style="color: green;">Urination On:</span>' +
        '<span class="tooltip-value">' +
        textLines[0] +
        '</span>' +
        '</div>' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label" style="color: red;">Urination Off:</span>' +
        '<span class="tooltip-value">' +
        textLines[1] +
        '</span>' +
        '</div>' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label" style="color: green;">Bowel On:</span>' +
        '<span class="tooltip-value">' +
        textLines[2] +
        '</span>' +
        '</div>' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label" style="color: red;">Bowel Off:</span>' +
        '<span class="tooltip-value">' +
        textLines[3] +
        '</span>' +
        '</div>' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label" style="color: gray;">No Void:</span>' +
        '<span class="tooltip-value">' +
        textLines[4] +
        '</span>' +
        '</div>' +
        staffMembersHTML +
        '</div>' +
        '</div>'
      );
    };
  }

  createCustomTooltipWholePartialInterval(
    graphData: GraphDataModel,
    staffMembers: DataTakerStaffDataModel[]
  ) {
    const matchingTrialsLabel =
      this.selectedGraphType ===
      TargetGraphType.WholePartialIntervalNotHappened
        ? 'Target Not Happened Trials:'
        : 'Target Correct Trials:';

    return function ({
      series,
      seriesIndex,
      dataPointIndex,
      w,
    }: {
      series: any[];
      seriesIndex: number;
      dataPointIndex: number;
      w: any;
    }) {
      const dataPoint =
        graphData.Series![seriesIndex].DataPoints![dataPointIndex];
      const totalTrials = dataPoint.RawValue1;
      const matchingTrials = dataPoint.RawValue2;

      const dateToDisplay = new Date(
        dataPoint.Date ?? new Date().getTime()
      ).toDateString();
      const seriesName = graphData.Series![seriesIndex].Name ?? 'No Name';
      const seriesColor =
        w.globals.colors[seriesIndex % w.globals.colors.length] ?? '#008FFB';

      const staffIDs = dataPoint.StaffIDs;
      const staffsForDataPoint = staffMembers.filter((staffMember) =>
        staffIDs?.includes(staffMember.StaffID!)
      );
      const staffMembersToDisplay = staffsForDataPoint
        .map((staffMember) => staffMember.FullName || 'No User')
        .join(', ');
      const staffMembersHTML =
        staffMembersToDisplay.length > 0
          ? '<div class="tooltip-item">' +
            '<span class="tooltip-label">Staff Members:</span>' +
            '<span class="tooltip-value">' +
            staffMembersToDisplay +
            '</span>' +
            '</div>'
          : '';

      if (
        dataPoint.Value === null ||
        totalTrials === null ||
        matchingTrials === null
      ) {
        return null;
      }

      return (
        '<div class="tooltip-card">' +
        '<div class="tooltip-card-header">' +
        '<h5 class="tooltip-card-title" style="color: ' +
        seriesColor +
        ';">' +
        seriesName +
        '<br>' +
        '(' +
        dateToDisplay +
        ')' +
        '</h5>' +
        '</div>' +
        '<div class="tooltip-card-body">' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Total Trials:</span>' +
        '<span class="tooltip-value">' +
        totalTrials +
        '</span>' +
        '</div>' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">' +
        matchingTrialsLabel +
        '</span>' +
        '<span class="tooltip-value">' +
        matchingTrials +
        '</span>' +
        '</div>' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Percentage Calculated:</span>' +
        '<span class="tooltip-value">' +
        dataPoint.Value +
        '%</span>' +
        '</div>' +
        staffMembersHTML +
        '</div>' +
        '</div>'
      );
    };
  }

  loadPercentageCorrectRespondingGraphOptions() {
    this.chartOptions = {
      series: this.graphData()!.Series!.map((series, index) =>
        this.mapLineSeriesForChart(series, this.graphData()!, index)
      ),
      colors: this.resolveChartColors(this.graphData()!.Series),
      legend: this.buildLegendConfig(this.displayLegend()),
      chart: {
        height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
        width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
        type: 'line',
        ...this.sharedChartConfig,
      },
      dataLabels: this.getDataLabelsConfig(),
      stroke: {
        curve: 'straight',
      },
      grid: {
        padding: {
          right: 30,
          left: 20,
        },
      },
      markers: this.getMarkersConfig(),
      tooltip: this.displayMoreDataOnTooltips()
        ? {
            custom: this.createCustomTooltipPrompt(
              this.graphData()!,
              this.staffMembers()
            ),
          }
        : {
            enabled: true,
            x: {
              format: 'MMM dd yyyy',
            },
            y: {
              formatter: (value: any) => {
                if (value === null) {
                  return '';
                }
                return value + ' %';
              },
            },
            hideEmptySeries: true,
          },
      title: {
        text: this.graphData()?.Title ?? 'Chart',
      },
      xaxis: {
        type: 'datetime',
        title: {
          text: this.graphData()?.XAxisTitle ?? 'X Axis',
        },
        labels: {
          datetimeUTC: false,
        },
      },
      yaxis: {
        min: 0,
        max: 100,
        title: {
          text: this.graphData()?.YAxisTitle ?? 'Y Axis',
        },
        labels: {
          formatter: (value: number) => {
            // If it's a whole number, don't show decimal places
            if (Number.isInteger(value)) {
              return value.toString();
            }
            // If it's a decimal, round to 2 decimal places
            return value.toFixed(2);
          },
        },
      },
      annotations: this.buildAnnotationsConfig(
        this.graphData()!,
        null,
        this.shouldRenderAnnotations()
      ),
    };
  }

  loadDurationPercentageOnGoalGraphOptions() {
    this.chartOptions = {
      series: this.graphData()!.Series!.map((series) => ({
        name: series.Name ?? 'No Name',
        data: series.DataPoints!.map(
          (data) =>
            [
              data.Date ? new Date(data.Date).getTime() : new Date().getTime(),
              data.Value,
            ] as [number, number | null]
        ),
      })),
      legend: {
        position: 'top',
        onItemClick: {
          toggleDataSeries: true,
        },
        onItemHover: {
          highlightDataSeries: true,
        },
      },
      chart: {
        height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
        width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
        type: 'line',
        ...this.sharedChartConfig,
      },
      dataLabels: this.getDataLabelsConfig(),
      stroke: {
        curve: 'straight',
      },
      grid: {
        padding: {
          right: 30,
          left: 20,
        },
      },
      markers: this.getMarkersConfig(),
      tooltip: {
        custom: this.createCustomTooltipPercentageOnGoal(
          this.graphData()!,
          this.staffMembers()
        ),
      },
      title: {
        text: this.graphData()?.Title ?? 'Chart',
      },
      xaxis: {
        type: 'datetime',
        title: {
          text: this.graphData()?.XAxisTitle ?? 'X Axis',
        },
        labels: {
          datetimeUTC: false,
        },
      },
      yaxis: {
        min: 0,
        max(max) {
          return max + 20;
        },
        title: {
          text: this.graphData()?.YAxisTitle ?? 'Y Axis',
        },
        labels: {
          formatter: (value: number) => {
            // If it's a whole number, don't show decimal places
            if (Number.isInteger(value)) {
              return value.toString();
            }
            // If it's a decimal, round to 2 decimal places
            return value.toFixed(2);
          },
        },
      },
      annotations: this.buildAnnotationsConfig(
        this.graphData()!,
        null,
        this.shouldRenderAnnotations()
      ),
    };
  }

  loadPromptPercentageDistributionGraphOptions() {
    // Calculate responsive width based on available space
    const availableWidth = window.innerWidth;
    let chartWidth: number;

    if (availableWidth < 768) {
      // Mobile: use most of the width but leave some padding
      chartWidth = Math.min(availableWidth * 0.9, 400);
    } else if (availableWidth < 1200) {
      // Tablet: use a reasonable fixed size
      chartWidth = Math.min(availableWidth * 0.6, 500);
    } else {
      // Desktop: use a maximum reasonable size
      chartWidth = Math.min(availableWidth * 0.4, 600);
    }

    this.chartOptions = {
      title: {
        text: this.graphData()?.Title ?? 'Chart',
      },
      series:
        this.graphData()?.Series!.map((series) => series.Value ?? 0) ?? [],
      chart: {
        width: chartWidth,
        type: 'pie',
      },
      colors:
        this.graphData()?.Series!.map((series) => series.Color ?? '#008FFB') ??
        [],
      labels:
        this.graphData()?.Series!.map((series) => series.Name ?? '') ?? [],
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: {
              width: Math.min(window.innerWidth * 0.9, 400),
            },
            legend: {
              position: 'bottom',
            },
          },
        },
        {
          breakpoint: 480,
          options: {
            chart: {
              width: Math.min(window.innerWidth * 0.95, 350),
            },
            legend: {
              position: 'bottom',
            },
          },
        },
      ],
    };
  }

  loadPromptCountsGraphOptions() {
    this.chartOptions = {
      title: {
        text: this.graphData()?.Title ?? 'Chart',
      },
      series: [
        {
          name: 'count',
          data:
            this.graphData()?.Series!.map((series) => series.Value ?? 0) ?? [],
        },
      ],
      chart: {
        height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
        width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
        type: 'bar',
        events: {
          click: function (chart, w, e) {
            // console.log(chart, w, e)
          },
        },
      },
      colors:
        this.graphData()?.Series!.map((series) => series.Color ?? '#008FFB') ??
        [],
      plotOptions: {
        bar: {
          columnWidth: '45%',
          distributed: true,
        },
      },
      dataLabels: {
        enabled: false,
      },
      legend: {
        show: false,
      },
      grid: {
        show: false,
      },
      xaxis: {
        categories:
          this.graphData()?.Series!.map((series) => series.Name ?? '') ?? [],
        labels: {
          style: {
            fontSize: '12px',
          },
          datetimeUTC: false,
        },
      },
    };
  }

  loadTaskAnalysisBarSimpleGraphOptions() {
    this.chartOptions = {
      series: [
        {
          name: 'Percentage',
          data:
            this.graphData()?.Series!.map((series) => series.Value ?? 0) ?? [],
        },
      ],
      chart: {
        type: "bar",
        height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
        width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
      },
      plotOptions: {
        bar: {
          horizontal: true,
        },
      },
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        categories:
          this.graphData()?.Series!.map((series) => series.Name ?? '') ?? [],
      },
      yaxis: {
        min: 0,
        max: 100,
      },
      tooltip: {
        y: {
          formatter: function (val: number) {
            return val + '%';
          },
        },
      },
    };
  }

  loadTaskAnalysisBarCountGraphOptions() {
    // for all the series, get all different prompt IDs and make a list of prompts ordered by unique prompt ID.
    const promptIDs =
      this.graphData()?.Series!.flatMap((series) =>
        series.DataPoints!.map((data) => data.RawValue1)
      ) ?? [];
    console.log(promptIDs);
    // remove duplicates
    const uniquePromptIDs = [...new Set(promptIDs)];
    console.log(uniquePromptIDs);

    this.chartOptions = {
      // for each prompt ID, create a series with the prompt name and the prompt count
      series: uniquePromptIDs.map((promptID) => {
        // name will have the TextValue from any datapoint from any series where RawValue1 matches the promptID
        const name =
          this.graphData()
            ?.Series!.find((series) =>
              series.DataPoints!.some((data) => data.RawValue1 === promptID)
            )
            ?.DataPoints!.find((data) => data.RawValue1 === promptID)
            ?.TextValue ?? '';

        // data will have a list of occurences of the promptID in all series
        const data =
          this.graphData()?.Series!.map(
            (series) =>
              series.DataPoints!.find((data) => data.RawValue1 === promptID)
                ?.Value ?? 0
          ) ?? [];

        // color will have the Color from any datapoint from any series where RawValue1 matches the promptID
        const color =
          this.graphData()
            ?.Series!.find((series) =>
              series.DataPoints!.some((data) => data.RawValue1 === promptID)
            )
            ?.DataPoints!.find((data) => data.RawValue1 === promptID)?.Color ??
          '#008FFB';

        return {
          name: name,
          data: data,
          color: color,
        };
      }),
      // {
      //   name: "Marine Sprite", // prompt names
      //   data: [44, 55], // prompt counts
      //   color: this.graphData()?.Series![0]?.Color ?? '#008FFB'
      // },
      chart: {
        type: "bar",
        height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
        width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
        stacked: true
      },
      plotOptions: {
        bar: {
          horizontal: true,
        },
      },
      stroke: {
        width: 1,
        colors: ['#fff'],
      },
      title: {
        text: this.graphData()?.Title ?? 'Chart',
      },
      xaxis: {
        categories:
          this.graphData()?.Series!.map((series) => series.Name ?? '') ?? [], // target names
      },
      yaxis: {
        title: {
          text: undefined,
        },
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return val + 'K';
          },
        },
      },
      legend: {
        position: 'top',
        horizontalAlign: 'left',
        offsetX: 40,
      },
    };
  }

  loadTaskAnalysisBarPercentageGraphOptions() {
    // for all the series, get all different prompt IDs and make a list of prompts ordered by unique prompt ID.
    const promptIDs =
      this.graphData()?.Series!.flatMap((series) =>
        series.DataPoints!.map((data) => data.RawValue1)
      ) ?? [];
    console.log(promptIDs);
    // remove duplicates
    const uniquePromptIDs = [...new Set(promptIDs)];
    console.log(uniquePromptIDs);

    this.chartOptions = {
      // for each prompt ID, create a series with the prompt name and the prompt count
      series: uniquePromptIDs.map((promptID) => {
        // name will have the TextValue from any datapoint from any series where RawValue1 matches the promptID
        const name =
          this.graphData()
            ?.Series!.find((series) =>
              series.DataPoints!.some((data) => data.RawValue1 === promptID)
            )
            ?.DataPoints!.find((data) => data.RawValue1 === promptID)
            ?.TextValue ?? '';

        // data will have a list of occurences of the promptID in all series
        const data =
          this.graphData()?.Series!.map(
            (series) =>
              series.DataPoints!.find((data) => data.RawValue1 === promptID)
                ?.Value ?? 0
          ) ?? [];

        // color will have the Color from any datapoint from any series where RawValue1 matches the promptID
        const color =
          this.graphData()
            ?.Series!.find((series) =>
              series.DataPoints!.some((data) => data.RawValue1 === promptID)
            )
            ?.DataPoints!.find((data) => data.RawValue1 === promptID)?.Color ??
          '#008FFB';

        return {
          name: name,
          data: data,
          color: color,
        };
      }),
      // {
      //   name: "Marine Sprite", // prompt names
      //   data: [44, 55], // prompt counts
      //   color: this.graphData()?.Series![0]?.Color ?? '#008FFB'
      // },
      chart: {
        type: "bar",
        height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
        width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
        stacked: true,
        stackType: '100%',
      },
      plotOptions: {
        bar: {
          horizontal: true,
        },
      },
      stroke: {
        width: 1,
        colors: ['#fff'],
      },
      title: {
        text: this.graphData()?.Title ?? 'Chart',
      },
      xaxis: {
        categories:
          this.graphData()?.Series!.map((series) => series.Name ?? '') ?? [], // target names
      },
      yaxis: {
        title: {
          text: undefined,
        },
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return val + 'K';
          },
        },
      },
      legend: {
        position: 'top',
        horizontalAlign: 'left',
        offsetX: 40,
      },
    };
  }

  loadTrialsByPromptsGraphOptions() {
    this.chartOptions = {
      series: this.graphData()!.Series!.map((series) => ({
        name: series.Name ?? 'No Name',
        data: series.DataPoints!.map(
          (data) =>
            [
              data.Date ? new Date(data.Date).getTime() : new Date().getTime(),
              data.Value,
            ] as [number, number | null]
        ),
        color: series.Color ?? '#008FFB',
      })),
      legend: this.displayLegend()
        ? {
            position: 'top',
            onItemClick: {
              toggleDataSeries: true,
            },
            onItemHover: {
              highlightDataSeries: true,
            },
          }
        : {
            show: false,
          },
      chart: {
        height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
        width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
        type: 'line',
        ...this.sharedChartConfig,
      },
      dataLabels: this.getDataLabelsConfig(),
      stroke: {
        curve: 'straight',
      },
      grid: {
        padding: {
          right: 30,
          left: 20,
        },
      },
      markers: this.getMarkersConfig(),
      tooltip: {
        x: {
          format: 'MMM dd yyyy',
        },
        y: {
          formatter: (value: any) => {
            if (value === null) {
              return '';
            }
            return value;
          },
        },
      },
      title: {
        text: this.graphData()?.Title ?? 'Chart',
      },
      xaxis: {
        type: 'datetime',
        title: {
          text: this.graphData()?.XAxisTitle ?? 'X Axis',
        },
        labels: {
          datetimeUTC: false,
        },
      },
      yaxis: {
        title: {
          text: this.graphData()?.YAxisTitle ?? 'Y Axis',
        },
        labels: {
          formatter: (value: number) => {
            // If it's a whole number, don't show decimal places
            if (Number.isInteger(value)) {
              return value.toString();
            }
            // If it's a decimal, round to 2 decimal places
            return value.toFixed(2);
          },
        },
      },
      annotations: this.buildAnnotationsConfig(
        this.graphData()!,
        null,
        this.shouldRenderAnnotations()
      ),
    };
  }

  loadDailyPromptPercentageGraphOptions() {
    this.chartOptions = {
      series: this.graphData()!.Series!.map((series) => ({
        name: series.Name ?? 'No Name',
        data: series.DataPoints!.map(
          (data) =>
            [
              data.Date ? new Date(data.Date).getTime() : new Date().getTime(),
              data.Value,
            ] as [number, number | null]
        ),
        color: series.Color ?? '#008FFB',
        hidden: series.IsHiddenByDefault ?? false,
      })),
      legend: this.displayLegend()
        ? {
            position: 'top',
            onItemClick: {
              toggleDataSeries: true,
            },
            onItemHover: {
              highlightDataSeries: true,
            },
          }
        : {
            show: false,
          },
      chart: {
        height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
        width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
        type: 'line',
        ...this.sharedChartConfig,
      },
      dataLabels: this.getDataLabelsConfig(),
      stroke: {
        curve: 'straight',
      },
      grid: {
        padding: {
          right: 30,
          left: 20,
        },
      },
      markers: this.getMarkersConfig(),
      tooltip: {
        x: {
          format: 'MMM dd yyyy',
        },
        y: {
          formatter: (value: any) => {
            if (value === null) {
              return '';
            }
            return value + ' %';
          },
        },
      },
      title: {
        text: this.graphData()?.Title ?? 'Chart',
      },
      xaxis: {
        type: 'datetime',
        title: {
          text: this.graphData()?.XAxisTitle ?? 'X Axis',
        },
        labels: {
          datetimeUTC: false,
        },
      },
      yaxis: {
        title: {
          text: this.graphData()?.YAxisTitle ?? 'Y Axis',
        },
        labels: {
          formatter: (value: number) => {
            // If it's a whole number, don't show decimal places
            if (Number.isInteger(value)) {
              return value.toString();
            }
            // If it's a decimal, round to 2 decimal places
            return value.toFixed(2);
          },
        },
      },
      annotations: this.buildAnnotationsConfig(
        this.graphData()!,
        null,
        this.shouldRenderAnnotations()
      ),
    };
  }

  loadFrequencySimpleGraphOptions() {
    this.chartOptions = {
      series: this.graphData()!.Series!.map((series, index) => ({
        name: series.Name ?? 'No Name',
        data: series.DataPoints!.map(
          (data) =>
            [
              data.Date ? new Date(data.Date).getTime() : new Date().getTime(),
              data.Value,
            ] as [number, number | null]
        ),
        color: this.resolveSeriesColor(series, index),
      })),
      colors: this.resolveChartColors(this.graphData()!.Series),
      legend: this.buildLegendConfig(this.displayLegend()),
      chart: {
        height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
        width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
        type: 'line',
        ...this.sharedChartConfig,
      },
      dataLabels: this.getDataLabelsConfig(),
      stroke: {
        curve: 'straight',
      },
      grid: {
        padding: {
          right: 30,
          left: 20,
        },
      },
      markers: this.getMarkersConfig(),
      tooltip: {
        custom: this.createCustomTooltipSimple(
          this.graphData()!,
          this.staffMembers()
        ),
      },
      title: {
        text: this.graphData()?.Title ?? 'Chart',
      },
      xaxis: {
        type: 'datetime',
        title: {
          text: this.graphData()?.XAxisTitle ?? 'X Axis',
        },
        labels: {
          datetimeUTC: false,
        },
      },
      yaxis: {
        title: {
          text: this.graphData()?.YAxisTitle ?? 'Y Axis',
        },
        labels: {
          formatter: (value: number) => {
            // If it's a whole number, don't show decimal places
            if (Number.isInteger(value)) {
              return value.toString();
            }
            // If it's a decimal, round to 2 decimal places
            return value.toFixed(2);
          },
        },
      },
      annotations: this.buildAnnotationsConfig(
        this.graphData()!,
        null,
        this.shouldRenderAnnotations()
      ),
    };
  }

  loadDurationCountGraphOptions() {
    this.chartOptions = {
      series: this.graphData()!.Series!.map((series) => ({
        name: series.Name ? series.Name : 'No Name',
        data: series.DataPoints!.map(
          (data) =>
            [
              data.Date ? new Date(data.Date).getTime() : new Date().getTime(),
              data.Value,
            ] as [number, number | null]
        ),
      })),
      legend: {
        position: 'top',
        onItemClick: {
          toggleDataSeries: true,
        },
        onItemHover: {
          highlightDataSeries: true,
        },
      },
      chart: {
        height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
        width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
        type: 'line',
        ...this.sharedChartConfig,
      },
      dataLabels: this.getDataLabelsConfig(),
      stroke: {
        curve: 'straight',
      },
      grid: {
        padding: {
          right: 30,
          left: 20,
        },
      },
      markers: this.getMarkersConfig(),
      tooltip: {
        custom: this.createCustomTooltipSimple(
          this.graphData()!,
          this.staffMembers()
        ),
      },
      title: {
        text: this.graphData()?.Title ?? 'Chart',
      },
      xaxis: {
        type: 'datetime',
        title: {
          text: this.graphData()?.XAxisTitle ?? 'X Axis',
        },
        labels: {
          datetimeUTC: false,
        },
      },
      yaxis: {
        title: {
          text: this.graphData()?.YAxisTitle ?? 'Y Axis',
        },
        labels: {
          formatter: (value: number) => {
            // If it's a whole number, don't show decimal places
            if (Number.isInteger(value)) {
              return value.toString();
            }
            // If it's a decimal, round to 2 decimal places
            return value.toFixed(2);
          },
        },
      },
      annotations: this.buildAnnotationsConfig(
        this.graphData()!,
        null,
        this.shouldRenderAnnotations()
      ),
    };
  }

  loadQuantityLineGraphOptions() {
    this.chartOptions = {
      series: this.graphData()!.Series!.map((series) => ({
        name: series.Name ? series.Name : 'No Name',
        data: this.processSeriesDataWithBreaks(series, this.graphData()!).data,
      })),
      legend: {
        position: 'top',
        onItemClick: {
          toggleDataSeries: true,
        },
        onItemHover: {
          highlightDataSeries: true,
        },
      },
      chart: {
        height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
        width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
        type: 'line',
        ...this.sharedChartConfig,
      },
      dataLabels: this.getDataLabelsConfig(),
      stroke: {
        curve: 'straight',
      },
      grid: {
        padding: {
          right: 30,
          left: 20,
        },
      },
      markers: this.getMarkersConfig(),
      tooltip: {
        custom: this.createCustomTooltipSimple(
          this.graphData()!,
          this.staffMembers()
        ),
      },
      title: {
        text: this.graphData()?.Title ?? 'Chart',
      },
      xaxis: {
        type: 'datetime',
        title: {
          text: this.graphData()?.XAxisTitle ?? 'X Axis',
        },
        labels: {
          datetimeUTC: false,
        },
      },
      yaxis: {
        title: {
          text: this.graphData()?.YAxisTitle ?? 'Y Axis',
        },
        labels: {
          formatter: (value: number) => {
            // If it's a whole number, don't show decimal places
            if (Number.isInteger(value)) {
              return value.toString();
            }
            // If it's a decimal, round to 2 decimal places
            return value.toFixed(2);
          },
        },
      },
      annotations: this.buildAnnotationsConfig(
        this.graphData()!,
        null,
        this.shouldRenderAnnotations()
      ),
    };
  }

  loadFluencyLineGraphOptions() {
    this.chartOptions = {
      series: this.graphData()!.Series!.map((series) => ({
        name: series.Name ? series.Name : 'No Name',
        data: this.processSeriesDataWithBreaks(series, this.graphData()!).data,
      })),
      legend: {
        position: 'top',
        onItemClick: {
          toggleDataSeries: true,
        },
        onItemHover: {
          highlightDataSeries: true,
        },
      },
      chart: {
        height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
        width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
        type: 'line',
        ...this.sharedChartConfig,
      },
      dataLabels: this.getDataLabelsConfig(),
      stroke: {
        curve: 'straight',
      },
      grid: {
        padding: {
          right: 30,
          left: 20,
        },
      },
      markers: this.getMarkersConfig(),
      tooltip: {
        custom: this.createCustomTooltipSimple(
          this.graphData()!,
          this.staffMembers()
        ),
      },
      title: {
        text: this.graphData()?.Title ?? 'Chart',
      },
      xaxis: {
        type: 'datetime',
        title: {
          text: this.graphData()?.XAxisTitle ?? 'X Axis',
        },
        labels: {
          datetimeUTC: false,
        },
      },
      yaxis: {
        title: {
          text: this.graphData()?.YAxisTitle ?? 'Y Axis',
        },
        labels: {
          formatter: (value: number) => {
            // If it's a whole number, don't show decimal places
            if (Number.isInteger(value)) {
              return value.toString();
            }
            // If it's a decimal, round to 2 decimal places
            return value.toFixed(2);
          },
        },
      },
      annotations: this.buildAnnotationsConfig(
        this.graphData()!,
        null,
        this.shouldRenderAnnotations()
      ),
    };
  }

  loadDurationByTrialGraphOptions() {
    this.chartOptions = {
      // data: [[x, y, size]]
      series: this.graphData()!.Series!.map((series) => ({
        name: series.Name ?? 'No Name',
        data: series.DataPoints!.map(
          (data) =>
            [
              data.Date ? new Date(data.Date).getTime() : new Date().getTime(),
              data.Value,
              data.RawValue1,
            ] as [number, number, number]
        ),
        color: series.Color ?? '',
      })),
      chart: {
        height: 550,
        type: 'scatter',
        zoom: {
          type: 'xy',
        },
      },
      dataLabels: {
        enabled: false,
      },
      title: {
        text: this.graphData()?.Title ?? 'Chart',
      },
      grid: {
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
      xaxis: {
        type: 'datetime',
        labels: {
          datetimeUTC: false,
        },
      },
      yaxis: {
        min: (value: number) => {
          if (value < 5 * 3600) {
            return 5 * 3600;
          }
          return value - 1800;
        },
        max: (value: number) => {
          if (value > 20 * 3600) {
            return 20 * 3600;
          }
          return value + 1800;
        },
        labels: {
          formatter: (value: number) => {
            // format time from seconds to HH:MM:SS
            return Utils.formatTime(value);
          },
        },
      },
      tooltip: {
        custom: this.createCustomTooltipDurationByTrial(this.graphData()!),
      },
      annotations: {
        ...this.buildAnnotationsConfig(this.graphData()!, null, false),
        xaxis: this.shouldRenderAnnotations()
          ? this.xaxisAnnotationsFromGraphData(this.graphData()!)
          : [],
      },
    };
  }

  createCustomTooltipDurationByTrial(graphData: GraphDataModel) {
    return function ({
      series,
      seriesIndex,
      dataPointIndex,
      w,
    }: {
      series: any[];
      seriesIndex: number;
      dataPointIndex: number;
      w: any;
    }) {
      // should show the date and the duration of the target in minutes.
      const dataPoint =
        graphData.Series![seriesIndex].DataPoints![dataPointIndex];
      // the date should be in the format of "MMM dd yyyy - HH:MM:SS"
      const dateToDisplay = new Date(
        dataPoint.Date ?? new Date().getTime()
      ).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
      // time from the day start in seconds
      const timeFromDayStart = dataPoint.Value ?? 0;
      // time calculated from the day start
      const timeFromDayStartFormatted = Utils.formatTime(timeFromDayStart);
      const duration = dataPoint.RawValue1;
      const seriesName = graphData.Series![seriesIndex].Name ?? 'No Name';
      const seriesColor =
        w.globals.colors[seriesIndex % w.globals.colors.length] ?? '#008FFB';

      return (
        '<div class="tooltip-card">' +
        '<div class="tooltip-card-header">' +
        '<h5 class="tooltip-card-title" style="color: ' +
        seriesColor +
        ';">' +
        seriesName +
        '</h5>' +
        '</div>' +
        '<div class="tooltip-card-body">' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Date:</span>' +
        '<span class="tooltip-value">' +
        dateToDisplay +
        '</span>' +
        '</div>' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Duration (in Minutes):</span>' +
        '<span class="tooltip-value">' +
        duration +
        ' minutes' +
        '</span>' +
        '</div>' +
        // '<div class="tooltip-item">' +
        // '<span class="tooltip-label">Time from Day Start:</span>' +
        // '<span class="tooltip-value">' + timeFromDayStart + ' seconds' + '</span>' +
        // '</div>' +
        // '<div class="tooltip-item">' +
        // '<span class="tooltip-label">Time from Day Start (Formatted):</span>' +
        // '<span class="tooltip-value">' + timeFromDayStartFormatted + '</span>' +
        // '</div>' +
        '</div>' +
        '</div>'
      );
    };
  }

  // Annotation functions

  addAnnotationButtonClicked() {
    console.log('addAnnotationButtonClicked');
    const emptyAnnotation: GraphAnnotationModel = {
      PatientTargetIDs: [this.selectedTargets()[0]!.ID!],
    };
    this.annotationToEdit.set(emptyAnnotation);
    this.showAddAnnotation.set(true);
  }

  onToggleTrendLine() {
    this.loadDataForGraph(this.selectedTargets()[0]!.ID!);
  }

  onToggleSegmentTrendLineByPhase() {
    this.loadDataForGraph(this.selectedTargets()[0]!.ID!);
  }

  editAnnotation(annotation: GraphAnnotationModel) {
    console.log('editAnnotation', annotation);
    this.annotationToEdit.set(annotation);
    this.showAddAnnotation.set(true);
  }

  onCloseAddAnnotation(withChanges: boolean = false) {
    console.log('onCloseAddAnnotation', withChanges);
    if (withChanges) {
      this.loadData();
    }
    this.showAddAnnotation.set(false);
  }

  /**
   * Formats annotation label text to use multiple lines if it exceeds 30 characters
   */
  protected override formatAnnotationLabelText(
    text: string
  ): string | string[] {
    if (text.length <= 30) {
      return text;
    }

    // Split the text into words and filter out empty strings
    const words = text.split(' ').filter((word) => word.length > 0);
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      // If adding this word would exceed 30 characters, start a new line
      if (currentLine.length + word.length + 1 > 30 && currentLine.length > 0) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        if (currentLine.length === 0) {
          currentLine = word;
        } else {
          currentLine += ' ' + word;
        }
      }
    }

    // Add the last line if it has content
    if (currentLine.length > 0) {
      lines.push(currentLine);
    }

    // Return array of strings for multiline support in ApexCharts
    // Pad each line to exactly 30 characters with whitespace
    if (lines.length > 1) {
      return lines.map((line) => line.padEnd(30, ' '));
    }

    return text;
  }

  /**
   * Handles print click from the graph toolbar
   */
  override handleDownloadPngClick(): void {
    if (!this.graphData()) return;
    this.buildReportHeaderInfo().then((header) => {
      const isGrouped = this.isGrouped();
      if (isGrouped) {
        if (!this.chart) return;
        this.printService.downloadPngPage(this.chart as any, header);
      } else {
        const charts = this.chartList ? this.chartList.toArray() : [];
        if (charts.length === 0) return;
        this.printService.downloadPngPageForCharts(charts as any, header);
      }
    });
  }

  override handleDownloadPdfClick(): void {
    if (!this.graphData()) return;
    this.buildHeaderInfoAndDownloadPdf();
  }

  private async buildHeaderInfoAndDownloadPdf(): Promise<void> {
    this.isPrintingPdf = true;
    this.updateGraph();
    const headerInfo = await this.buildReportHeaderInfo();
    const isGrouped = this.isGrouped();
    if (isGrouped) {
      if (!this.chart) return;
      this.printService.downloadPdf(this.chart as any, headerInfo);
    } else {
      const charts = this.chartList ? this.chartList.toArray() : [];
      if (charts.length === 0) return;
      this.printService.downloadPdfForCharts(charts as any, headerInfo);
    }
    this.isPrintingPdf = false;
    this.updateGraph();
  }

  private async buildReportHeaderInfo(): Promise<PrintHeaderInfo> {
    let patientName = 'Unknown Patient';
    try {
      const patient = await this.staffApiService
        .GetPatientInfo({ id: Number(this.patientID()) })
        .pipe(take(1))
        .toPromise();
      if (patient) {
        patientName =
          `${patient.FirstName || ''} ${patient.LastName || ''}`.trim() ||
          patient.PatientName ||
          'Unknown Patient';
      }
    } catch {}
    const startDate = this.startDate();
    const endDate = this.endDate();
    const timePeriod = `${this.datePipe.transform(
      startDate,
      'MMM dd, yyyy'
    )} - ${this.datePipe.transform(endDate, 'MMM dd, yyyy')}`;
    const graphType = this.getGraphTypeDisplayName();
    const subtitleItems = this.selectedTargets().map(
      (t) =>
        `${t.TargetName || 'Unnamed Target'} (${Utils.patientTargetTypeLabel(
          t.TargetType!
        )})`
    );
    const selectedTargets = this.selectedTargets().map(
      (target) => target.TargetName || 'Unknown Target'
    );
    const visibleAnnotations = this.annotations()
      .filter((annotation) => {
        if (!this.isAnnotationDisplayed(annotation)) return false;
        if (annotation.StartDate) {
          const annotationDate = new Date(annotation.StartDate);
          return annotationDate >= startDate && annotationDate <= endDate;
        }
        return true;
      })
      .map((annotation) => this.annotationLabel(annotation));
    const groupingMap: { [key in GraphGrouping]: string } = {
      [GraphGrouping.BySession]: 'By Session',
      [GraphGrouping.Daily]: 'Daily',
      [GraphGrouping.Weekly]: 'Weekly',
      [GraphGrouping.Monthly]: 'Monthly',
      [GraphGrouping.Yearly]: 'Yearly',
      [GraphGrouping.ByStaffMember]: 'By Staff Member',
      [GraphGrouping.ByTrial]: 'By Trial',
    };
    // Get the first selected target name for the subtitle
    const firstSelectedTarget = this.selectedTargets().length > 0 
      ? this.selectedTargets()[0].TargetName || undefined 
      : undefined;
    
    const headerInfo: PrintHeaderInfo = {
      graphTitle: this.graphData()?.Title || 'Target Graph',
      graphType,
      componentType: 'Target Graph',
      patientName,
      timePeriod,
      groupingLabel: this.groupingOptionLabel(),
      displayOptions: this.getDisplayOptions(),
      subtitleItems,
      selectedTargets,
      selectedTarget: firstSelectedTarget,
      annotations: visibleAnnotations,
      annotationColorLegends: this.getDeclutterLegendItemsForExport(),
      staffMember: this.getStaffMember() ?? undefined,
    };
    return headerInfo;
  }

  private groupingOptionLabel(): string {
    const groupingMap: { [key in GraphGrouping]: string } = {
      [GraphGrouping.BySession]: 'By Session',
      [GraphGrouping.Daily]: 'Daily',
      [GraphGrouping.Weekly]: 'Weekly',
      [GraphGrouping.Monthly]: 'Monthly',
      [GraphGrouping.Yearly]: 'Yearly',
      [GraphGrouping.ByStaffMember]: 'By Staff Member',
      [GraphGrouping.ByTrial]: 'By Trial',
    };
    return groupingMap[this.groupingOption];
  }

  private getStaffMember(): string | undefined {
    return this.selectedStaffMember?.FullName ?? undefined;
  }

  private getDisplayOptions(): string[] {
    const options: string[] = [];
    if (this.shouldDisplayToggleGrouped()) {
      if (this.isGrouped()) options.push('Grouped');
      else options.push('Synced');
    }
    if (this.shouldDisplayMergeSimilarPromptsOptions()) {
      if (this.mergeSimilarPrompts()) options.push('Merge Similar Prompts');
    }
    if (this.shouldDisplayShowNotTrackedAsZeroes()) {
      if (this.showNotTrackedAsZeroes()) options.push('Not Tracked as Zeroes');
    }
    if (this.shouldDisplayDataPointNumbersOption()) {
      options.push(`Series: ${this.seriesDataLabelOption()}`);
    }
    if (this.displayTrendLine()) {
      options.push(`Trend Line: ${this.trendLineDataLabelOption()}`);
    }
    if (this.shouldDisplayAnnotationLabelBackgroundOption()) {
      options.push(`Annotation Labels: ${this.annotationLabelBackgroundOption()}`);
    }
    if (this.declutterAnnotations()) {
      options.push('Declutter Annotations');
    }
    return options;
  }

  /**
   * Gets the display name for the current graph type
   */
  private getGraphTypeDisplayName(): string {
    if (
      this.selectedGraphType === null ||
      this.selectedGraphType === undefined
    ) {
      return 'Unknown Graph Type';
    }

    const graphTypeMap: { [key in TargetGraphType]: string } = {
      [TargetGraphType.PercentageCorrectResponding]:
        'Percentage Correct Responding',
      [TargetGraphType.PromptPercentageDistribution]:
        'Prompt Percentage Distribution',
      [TargetGraphType.PromptCounts]: 'Prompt Counts',
      [TargetGraphType.TrialsByPrompts]: 'Trials by Prompts',
      [TargetGraphType.DailyPromptPercentage]: 'Daily Prompt Percentage',
      [TargetGraphType.TimeOfDay]: 'Time of Day',
      [TargetGraphType.FrequencySimple]: 'Frequency Simple',
      [TargetGraphType.DurationSum]: 'Duration Sum',
      [TargetGraphType.DurationCount]: 'Duration Count',
      [TargetGraphType.QuantitySum]: 'Quantity Sum',
      [TargetGraphType.QuantityCount]: 'Quantity Count',
      [TargetGraphType.FluencyAverage]: 'Fluency Average',
      [TargetGraphType.FluencyIndividual]: 'Fluency Individual',
      [TargetGraphType.FluencyPercentCorrectWithElapsedTime]:
        'Fluency % Correct with Elapsed Time',
      [TargetGraphType.GroupedFrequency]: 'Grouped Frequency',
      [TargetGraphType.TaskAnalysisLine]: 'Task Analysis Line',
      [TargetGraphType.ConditionalDescrimination]: 'Conditional Discrimination',
      [TargetGraphType.CombinationSeparated]: 'Combination Separated',
      [TargetGraphType.CombinationMerged]: 'Combination Merged',
      [TargetGraphType.DurationPercentageOnSession]:
        'Duration Percentage on Session',
      [TargetGraphType.TaskAnalysisBarSimple]: 'Task Analysis Bar Simple',
      [TargetGraphType.TaskAnalysisBarPromptCount]:
        'Task Analysis Bar Prompt Count',
      [TargetGraphType.TaskAnalysisBarPromptPercentage]:
        'Task Analysis Bar Prompt Percentage',
      [TargetGraphType.ToiletingLineColumn]: 'Toileting Line Column',
      [TargetGraphType.ToiletingBar]: 'Toileting Bar',
      [TargetGraphType.ToiletingPie]: 'Toileting Pie',
      [TargetGraphType.ToiletingPercentageCorrect]:
        'Toileting Percentage Correct',
      [TargetGraphType.ToiletingAccidentsVsSuccesses]:
        'Toileting Accidents vs. Successes',
      [TargetGraphType.WholePartialIntervalCorrect]:
        'Whole Partial Interval Correct',
      [TargetGraphType.WholePartialIntervalNotHappened]:
        'Whole Partial Interval Not Happened',
      [TargetGraphType.DurationTrialsTimeOfDay]: 'Duration Trials - Time of Day',
      [TargetGraphType.DurationPercentageOnGoal]: 'Duration Percentage on Goal',
      [TargetGraphType.Currency]: 'Currency',
      [TargetGraphType.DurationByTrial]: 'Duration By Trial',
      [TargetGraphType.TrialCount]: 'Trial Count',
      [TargetGraphType.Anecdotal]: 'Anecdotal Count',
    };

    return graphTypeMap[this.selectedGraphType] || 'Unknown Graph Type';
  }
}
